import {
	FloatingActionButton,
	SearchInputForm,
	SearchPlaceholder,
	Tabs,
	ToolbarAction,
	ToolbarRegularAction,
	Typography,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	ActionGroup,
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { partition } from "client_legacy/ndaw/arrayHelpers";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	getViewModel,
	MoveUsersToRecycleBinResponse,
	PermanentDeleteUsersResponse,
	RecycleBinUser,
	RestoreUsersResponse,
	sendMoveUsersToRecycleBinRequest,
	sendPermanentDeleteUsersRequest,
	sendRestoreUsersRequest,
	sendWelcomeEmail,
	UserGridItem,
	UserManagementActionsInfoModel
} from "client/Api/UserManagement/userManagement";
import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { UserOverview, userOverviewSearchPropertyKeys } from "../../Users/UserOverview";
import styles from "./UserManagementPage.module.less";
import { ImportTarget } from "client/Api/ImportUsers/importUsers";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { importUsersRouting } from "./ImportUserPage";
import {
	connectToRouter,
	Navigation,
	PredefinedPropOptionKind,
	route
} from "client/Navigation";
import { EditUserPageRouteParams } from "./EditUserPage";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { ManageWelcomeEmailDialog } from "./ManageWelcomeEmailDialog";
import { ManageCustomFieldsDialog } from "./ManageCustomFieldsDialog";

export type UserManagementPageProps = {
	onNavigate(navigation: Navigation | string): void;
};

export const UserManagementPage = (props: UserManagementPageProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [activeTabId, setActiveTabId] = useState<TabId>("users");
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isManageWelcomeEmailDialogOpen, setIsManageWelcomeEmailDialogOpen] = useState<boolean>(false);
	const [isManageCustomFieldsDialogOpen, setIsManageCustomFieldsDialogOpen] = useState<boolean>(false);

	const [allUsers, setAllUsers] = useState<readonly UserGridItem[]>([]);

	const [users, recyclebinUsers] = useMemo(
		(): [readonly UserGridItem[], readonly UserGridItem[]] => {
			return partition(
				allUsers,
				(x) => isNil(x.deletionDate)
			);
		},
		[allUsers]
	);

	const [selection, setSelection] = useState<readonly UserGridItem[]>([]);
	const [recyclebinSelection, setRecyclebinSelection] = useState<readonly UserGridItem[]>([]);
	const [viewModel, setViewModel] = useState<ViewModel>(defaultViewModel);

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const [confirmationDialog, showConfirmationDialog] = useConfirmationDialog();

	useActiveSettingsMenuItem(SettingsPageKind.UserManagement);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const {
						allUsers,
						canConfigureTheWelcomeEmailPreference,
						currentUserId,
						userManagementActionsInfo,
						usersImportTarget,
						canConfigureCustomFields
					} = await getViewModel(signal)
						.catch(handlePageDataLoadingError);

					setAllUsers(allUsers);
					setViewModel({
						currentUserId,
						canConfigureTheWelcomeEmailPreference,
						usersImportTarget,
						userManagementActionsInfo,
						canConfigureCustomFields
					});

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const deleteHandler = useCallback(
		async (selection: readonly UserGridItem[]): Promise<void> => {
			const result: TryResult<void> = await showConfirmationDialog({
				title: localize(
					selection.length === 1
						? "_AreYouSureYouWantToDeleteThisUser"
						: "_AreYouSureYouWantToDeleteAllSelectedUsersExceptYourself"
				),
				confirmText: localize("_Delete"),
				trackingId: "delete-users-confirmation-dialog",
				children: (
					<>
						<span>
							{localize(
								selection.length === 1
									? "_WhenDoneThisUserWillNotBeAbleToLoginAnymore"
									: "_WhenDoneTheseUsersWillNotBeAbleToLoginAnymore"
							)}
						</span>
						<br />
						<Typography weight="bold">
							{localize(
								selection.length === 1
									? "_DeleteExistingUsersNote"
									: "_NoteTheseUsersWillNotBeDeletedFromOffice365"
							)}
						</Typography>
					</>
				)
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				const userIds: readonly number[] = selection.filter(u => u.id !== ensureNotNil(viewModel.currentUserId))
					.map(u => u.id);

				const response: MoveUsersToRecycleBinResponse = await sendMoveUsersToRecycleBinRequest(userIds, globalCancellation);

				const deletedUserDataById: Map<number, RecycleBinUser> = fromArray(
					response.usersMovedToRecycleBin,
					(x) => x.userId
				);

				setAllUsers(
					(x): readonly UserGridItem[] => {
						return x.map(
							(u): UserGridItem => {
								const data: RecycleBinUser | undefined = deletedUserDataById.get(u.id);

								if (isNil(data)) {
									return u;
								}

								return {
									...u,
									deletionDate: data.deletionDate,
									permanentDeletionDate: data.permanentDeletionDate
								};
							}
						);
					}
				);

				if (response.failedUserIds.length > 0) {
					alertManager.showError(localize("_NotAllUsersWereDeletedPleaseTryAgain"));
				} else {
					alertManager.showSuccess(
						localize(
							userIds.length === 1
								? "_UserDeleted"
								: "_UsersDeleted"
						)
					);
				}

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_FailedToDeleteUsers"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			viewModel.currentUserId,
			showConfirmationDialog
		]
	);

	const permanentDeleteHandler = useCallback(
		async (selection: readonly UserGridItem[]): Promise<void> => {
			const result: TryResult<void> = await showConfirmationDialog({
				title: localize("_AreYouSureYouWantToPermanentDeleteThisUsers"),
				confirmText: localize("_DeletePermanentlyAction"),
				trackingId: "permanent-delete-users-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				const userIds: readonly number[] = selection.filter(u => u.id !== ensureNotNil(viewModel.currentUserId))
					.map(u => u.id);

				const response: PermanentDeleteUsersResponse = await sendPermanentDeleteUsersRequest(
					userIds,
					globalCancellation
				);

				const deletedUserIds: Set<number> = new Set<number>(
					userIds.filter(
						x => !response.failedUserIds
							.includes(x)
					)
				);

				setAllUsers(
					x => x.filter(y => !deletedUserIds.has(y.id))
				);

				if (response.failedUserIds.length > 0) {
					alertManager.showError(localize("_NotAllUsersWereDeletedPermanentlyPleaseTryAgain"));
				} else {
					alertManager.showSuccess(localize("_UsersSuccessfullyDeletedPermanently"));
				}

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
				alertManager.showError(localize("_FailedToDeleteUsers"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			viewModel.currentUserId,
			showConfirmationDialog
		]
	);

	const restoreHandler = useCallback(
		async (selection: readonly UserGridItem[]): Promise<void> => {
			const result: TryResult<void> = await showConfirmationDialog({
				title: localize("_AreYouSureYouWantToRestoreThisUsers"),
				confirmText: localize("_Restore"),
				trackingId: "restore-users-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				const userIds: readonly number[] = selection.filter(u => u.id !== ensureNotNil(viewModel.currentUserId))
					.map(u => u.id);

				const response: RestoreUsersResponse = await sendRestoreUsersRequest(userIds, globalCancellation);

				const restoredUserIds: Set<number> = new Set<number>(
					userIds.filter(
						x => !response.failedUserIds
							.includes(x)
					)
				);

				setAllUsers(
					(x): readonly UserGridItem[] => {
						return x.map(
							(u): UserGridItem => {
								if (!restoredUserIds.has(u.id)) {
									return u;
								}

								return {
									...u,
									deletionDate: null,
									permanentDeletionDate: null
								};
							}
						);
					}
				);

				if (response.failedUserIds.length > 0) {
					alertManager.showError(localize("_NotAllUsersWereRestoredPleaseTryAgain"));
				} else {
					alertManager.showSuccess(localize("_UsersSuccessfullyRestored"));
				}

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			viewModel.currentUserId,
			showConfirmationDialog
		]
	);

	const sendWelcomeEmailHandler = useCallback(
		async (selection: readonly UserGridItem[]): Promise<void> => {
			const result: TryResult<void> = await showConfirmationDialog({
				title: localize("_SendWelcomeEmail"),
				confirmText: localize("_Send"),
				cancelText: localize("_Cancel"),
				trackingId: "send-welcome-email-confirmation-dialog",
				children: localize("_AreYouSureYouWantToSendTheEmail")
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await sendWelcomeEmail(
					selection.map(x => x.id),
					globalCancellation
				);

				alertManager.showSuccess(localize("_WelcomeEmailWasSuccessfullySent"));
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
				alertManager.showError(localize("_FailedToSendWelcomeEmail"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			showConfirmationDialog
		]
	);

	const deselectHandler = useCallback(
		(): void => {
			activeTabId === "users"
				? setSelection([])
				: setRecyclebinSelection([]);
		},
		[activeTabId]
	);

	const createHandler = useCallback(
		(): void => {
			onNavigate(userManagementRouting.create);
		},
		[onNavigate]
	);

	const handlers = useMemo(
		(): ActionHandlers => {
			return {
				onEdit: (selection: UserGridItem) => onNavigate(
					userManagementRouting.edit({ userId: `${selection.id}` })
				),
				onDelete: deleteHandler,
				onPermanentDelete: permanentDeleteHandler,
				onRestore: restoreHandler,
				onSendWelcomeEmail: sendWelcomeEmailHandler,
				onImportUsers: () => onNavigate(importUsersRouting.page),
				onImportUsersFromOffice365Groups: () => onNavigate(importUsersRouting.fromOffice365GroupsPage),
				onManageWelcomeEmail: () => setIsManageWelcomeEmailDialogOpen(true),
				onManageCustomFields: () => setIsManageCustomFieldsDialogOpen(true)
			};
		},
		[
			deleteHandler,
			permanentDeleteHandler,
			restoreHandler,
			sendWelcomeEmailHandler,
			onNavigate
		]
	);

	const actionContext = useMemo(
		(): ActionContext => {
			return {
				activeTabId,
				selection: activeTabId === "users"
					? selection
					: recyclebinSelection,
				canConfigureTheWelcomeEmailPreference: viewModel.canConfigureTheWelcomeEmailPreference,
				actionInfo: viewModel.userManagementActionsInfo,
				usersImportTarget: viewModel.usersImportTarget,
				currentUserId: viewModel.currentUserId,
				isMobile,
				canConfigureCustomFields: viewModel.canConfigureCustomFields
			};
		},
		[
			activeTabId,
			isMobile,
			recyclebinSelection,
			selection,
			viewModel
		]
	);

	const selectionActions = useMemo(
		(): readonly UserManagementAction[] => {
			return getSelectionActions({ deselectHandler });
		},
		[deselectHandler]
	);

	const contextualActions = useMemo(
		(): readonly UserManagementAction[] => {
			return listContextualActions(handlers);
		},
		[handlers]
	);

	const defaultActions = useMemo(
		(): readonly UserManagementAction[] => {
			return listDefaultActions(handlers);
		},
		[handlers]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<ActionContext> => {
			return getMainAction(createHandler);

		},
		[createHandler]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly UserManagementAction[] => {
			return buildActionsForTopBar<ActionContext, UserManagementAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions,
					defaultActions,
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			mainAction,
			selectionActions,
			contextualActions,
			defaultActions,
			hasSelection,
			isResponsive
		]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={actionContext}
			hasSelection={hasSelection}
			title={localize("_UserManagement")}
		>
			<SettingsPage.Section
				height="stretch"
			>
				<Tabs<TabId>
					activeTabId={activeTabId}
					onActiveTabIdChange={setActiveTabId}
					height="fill"
				>
					<Tabs.Tab<TabId>
						id="users"
						title={localize("_AllUsers")}
					>
						<TabContent
							data={users}
							selection={selection}
							onSelectionChange={setSelection}
						/>
					</Tabs.Tab>

					<Tabs.Tab<TabId>
						id="recyclebin"
						title={localize("_DeletedUsers")}
					>
						<TabContent
							data={recyclebinUsers}
							selection={recyclebinSelection}
							onSelectionChange={setRecyclebinSelection}
							isRecyclebin
						/>
					</Tabs.Tab>
				</Tabs>
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
			{mainAction.canExecute(actionContext) && (
				<>
					<div className="preserve-space-for-mobile-plus-button"/>
					<FloatingActionButton
						onClick={() => mainAction.execute(actionContext)}
						icon="plus"
						trackingId="new-button"
					/>
				</>
			)}
			{confirmationDialog}
			{isManageWelcomeEmailDialogOpen && (
				<ManageWelcomeEmailDialog
					onClose={() => setIsManageWelcomeEmailDialogOpen(false)}
				/>
			)}
			{isManageCustomFieldsDialogOpen && (
				<ManageCustomFieldsDialog
					onClose={() => setIsManageCustomFieldsDialogOpen(false)}
				/>
			)}
		</SettingsPage>
	);
};
UserManagementPage.displayName = "UserManagementPage";

export const UserManagementPageForRouter: FunctionComponent = connectToRouter(
	UserManagementPage,
	{
		onNavigate: PredefinedPropOptionKind.NavigateCallback
	}
);

type TabId = "users" | "recyclebin";

export type TabContentProps = {
	data: readonly UserGridItem[];
	selection: readonly UserGridItem[];
	onSelectionChange(selection: readonly UserGridItem[]): void;
	isRecyclebin?: boolean;
};

export const TabContent = (props: TabContentProps): ReactElement => {
	const {
		data,
		selection,
		onSelectionChange,
		isRecyclebin = false
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		version: newGuid(),
		value: ""
	});

	return (
		<div className={styles.tabContent}>
			<SettingsPage.SearchSection>
				<SearchInputForm
					trackingId="search"
					value={searchTerm}
					onChange={(value, version) => setSearchTerm({ version, value })}
					height="large"
					icon="filter"
					placeholder={(
						<SearchPlaceholder
							kind="filter"
							properties={userOverviewSearchPropertyKeys.map(localize)}
						/>
					)}
				/>
			</SettingsPage.SearchSection>

			<UserOverview
				data={data}
				selection={selection}
				onSelectionChange={onSelectionChange}
				searchTerm={searchTerm.value}
				showDeletionColumns={isRecyclebin}
				height="stretch"
			/>
		</div>
	);
};
TabContent.displayName = "TabContent";

type ActionContext = {
	activeTabId: TabId;
	selection: readonly UserGridItem[];
	canConfigureTheWelcomeEmailPreference: boolean;
	actionInfo: UserManagementActionsInfoModel;
	usersImportTarget: ImportTarget;
	currentUserId: number | null;
	isMobile: boolean;
	canConfigureCustomFields: boolean;
};

type UserManagementAction = ToolbarAction<ActionContext>;

type ActionHandlers = {
	onEdit: (selection: UserGridItem) => void;
	onDelete: (selection: readonly UserGridItem[]) => void;
	onPermanentDelete: (selection: readonly UserGridItem[]) => void;
	onRestore: (selection: readonly UserGridItem[]) => void;
	onSendWelcomeEmail: (selection: readonly UserGridItem[]) => void;
	onImportUsers: () => void;
	onImportUsersFromOffice365Groups: () => void;
	onManageWelcomeEmail: () => void;
	onManageCustomFields: () => void;
};

const importActionGroup: ActionGroup = {
	kind: "regular",
	name: "import",
	title: localize("_Import"),
	icon: "cloud-arrow-down"
};

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(
	handlers: SelectionActionsHandlers
): readonly UserManagementAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselect-all",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

function listDefaultActions(
	handlers: ActionHandlers
): readonly UserManagementAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "import-from-office365",
			title: localize("_FromOffice365"),
			canExecute: (c) => c.usersImportTarget === ImportTarget.Workspace365
				&& c.actionInfo
					.allowImportFromOffice365,
			execute: () => handlers.onImportUsers(),
			group: importActionGroup
		},
		{
			kind: ActionKind.Regular,
			name: "import-from-office365-groups",
			title: localize("_FromOffice365Groups"),
			canExecute: (c) => c.usersImportTarget === ImportTarget.Workspace365
				&& c.actionInfo
					.allowImportFromOffice365,
			execute: () => handlers.onImportUsersFromOffice365Groups(),
			group: importActionGroup
		},
		{
			kind: ActionKind.Regular,
			name: "manage-custom-fields",
			title: localize("_ManageCustomFields"),
			canExecute: (c) => c.canConfigureCustomFields,
			execute: () => handlers.onManageCustomFields(),
			icon: "gear"
		},
		{
			kind: ActionKind.Regular,
			name: "manage-welcome-email",
			title: localize("_ManageWelcomeEmail"),
			canExecute: (c) => c.canConfigureTheWelcomeEmailPreference,
			execute: () => handlers.onManageWelcomeEmail(),
			icon: "envelope"
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<ActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "new",
		title: localize("_New"),
		canExecute: (c) => c.actionInfo
			.allowCreate,
		execute: () => newHandler(),
		icon: "plus",
		isPrimary: true
	};
}

function listContextualActions(
	handlers: ActionHandlers
): readonly UserManagementAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.activeTabId === "users"
				&& c.selection.length === 1,
			execute: (c) => handlers.onEdit(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.activeTabId === "users"
				&& isNotNil(c.currentUserId)
				&& (
					c.selection.length > 1
					|| (
						c.selection.length === 1
							&& c.selection[0].id !== c.currentUserId
					)
				)
				&& c.actionInfo
					.allowDelete,
			execute: (c) => handlers.onDelete(c.selection),
			icon: "trash-can"
		},
		{
			kind: ActionKind.Regular,
			name: "permanent-delete",
			title: localize("_DeletePermanentlyAction"),
			canExecute: (c) => c.activeTabId === "recyclebin"
				&& isNotNil(c.currentUserId)
				&& c.selection.length >= 1
				&& c.actionInfo
					.allowDelete,
			execute: (c) => handlers.onPermanentDelete(c.selection),
			icon: "trash-can"
		},
		{
			kind: ActionKind.Regular,
			name: "restore",
			title: localize("_Restore"),
			canExecute: (c) => c.activeTabId === "recyclebin"
				&& isNotNil(c.currentUserId)
				&& c.selection.length >= 1
				&& c.actionInfo
					.allowDelete,
			execute: (c) => handlers.onRestore(c.selection),
			icon: "arrow-rotate-right"
		},
		{
			kind: ActionKind.Regular,
			name: "send-welcome-email",
			title: localize("_SendWelcomeEmail"),
			canExecute: (c) => c.activeTabId === "users"
				&& c.selection.length >= 1
				&& c.actionInfo
					.allowSendingWelcomeEmails,
			execute: (c) => handlers.onSendWelcomeEmail(c.selection),
			icon: "paper-plane"
		}
	];
}

type ViewModel = {
	currentUserId: number | null;
	usersImportTarget: ImportTarget;
	canConfigureTheWelcomeEmailPreference: boolean;
	userManagementActionsInfo: UserManagementActionsInfoModel;
	canConfigureCustomFields: boolean;
};

const defaultViewModel: ViewModel = {
	currentUserId: null,
	canConfigureTheWelcomeEmailPreference: false,
	usersImportTarget: ImportTarget.No,
	userManagementActionsInfo: {
		allowCreate: false,
		allowDelete: false,
		allowImportFromOffice365: false,
		allowSendingWelcomeEmails: false
	},
	canConfigureCustomFields: false
};

export const userManagementRouting = {
	overview: "/UserManagement",
	create: "/UserManagement/CreateUser",
	edit: route<EditUserPageRouteParams>`/UserManagement/EditUser/${"userId"}`
};
